
<template>
  <div
    class="pa-layout"
    :class="{ 'pa-sidebar-collapsed': sidebarCollapsed }"
  >

    <!-- MOBILE OVERLAY -->
    <div
      v-if="mobileSidebarOpen"
      class="pa-overlay"
      @click="closeMobileSidebar"
    ></div>

    <!-- SIDEBAR -->
    <DashboardSidebar
      :collapsed="sidebarCollapsed"
      :mobile-open="mobileSidebarOpen"
      @toggle-collapse="toggleSidebar"
      @close-mobile="closeMobileSidebar"
    />

    <!-- CONTENT AREA -->
    <div class="pa-content">

      <DashboardTopbar
        :collapsed="sidebarCollapsed"
        :user="currentUser"
        @toggle-collapse="toggleSidebar"
        @toggle-mobile="toggleMobileSidebar"
        @logout="logout"
      />

      <!-- PAGE CONTENT -->
      <div class="pa-main-content">
        <router-view />
      </div>

      <!-- FOOTER -->
      <footer class="pa-footer">
        <span>
          © {{ currentYear }} ProduPneu.fr
        </span>

        <span>
          Administration ProduPneu
          <i></i>
          Version 1.0
        </span>
      </footer>

    </div>
  </div>
</template>

<script>
import DashboardSidebar from
  "../components/dashboard/DashboardSidebar.vue";

import DashboardTopbar from
  "../components/dashboard/DashboardTopbar.vue";

export default {
  name: "AdminLayout",

  components: {
    DashboardSidebar,
    DashboardTopbar,
  },

  data() {
    return {
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      previousBodyOverflow: "",
    };
  },

  computed: {
    currentUser() {
      const auth = this.$store.state.auth;

      return auth && auth.user
        ? auth.user
        : {};
    },

    currentYear() {
      return new Date().getFullYear();
    },
  },

  watch: {
    "$route.path"() {
      this.closeMobileSidebar();
    },

    mobileSidebarOpen(isOpen) {
      document.body.style.overflow = isOpen
        ? "hidden"
        : this.previousBodyOverflow;
    },
  },

  created() {
    try {
      this.sidebarCollapsed =
        localStorage.getItem("produpneu_admin_sidebar") === "collapsed";
    } catch (error) {
      this.sidebarCollapsed = false;
    }
  },

  mounted() {
    this.previousBodyOverflow = document.body.style.overflow;

    window.addEventListener(
      "keydown",
      this.handleKeydown
    );

    window.addEventListener(
      "resize",
      this.handleResize
    );
  },

  beforeDestroy() {
    window.removeEventListener(
      "keydown",
      this.handleKeydown
    );

    window.removeEventListener(
      "resize",
      this.handleResize
    );

    document.body.style.overflow =
      this.previousBodyOverflow;
  },

  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;

      try {
        localStorage.setItem(
          "produpneu_admin_sidebar",
          this.sidebarCollapsed ? "collapsed" : "expanded"
        );
      } catch (error) {
        // Layout remains functional without localStorage.
      }
    },

    toggleMobileSidebar() {
      this.mobileSidebarOpen = !this.mobileSidebarOpen;
    },

    closeMobileSidebar() {
      this.mobileSidebarOpen = false;
    },

    handleKeydown(event) {
      if (event.key === "Escape") {
        this.closeMobileSidebar();
      }
    },

    handleResize() {
      if (window.innerWidth >= 992) {
        this.closeMobileSidebar();
      }
    },

    async logout() {
      // Your existing Vuex auth module must define auth/logout.
      // Do not redirect before logout has actually succeeded.
      try {
        await this.$store.dispatch("auth/logout");

        this.$router.push("/login").catch(error => {
          if (error.name !== "NavigationDuplicated") {
            console.error(error);
          }
        });
      } catch (error) {
        console.error("Erreur de déconnexion :", error);
        window.alert(
          "Impossible de vous déconnecter pour le moment."
        );
      }
    },
  },
};
</script>

<style scoped>
.pa-layout {
  --pa-sidebar-width: 260px;

  display: block;
  min-height: 100vh;
  width: 100%;
  background: #f5f7fa;
  font-family: "Poppins", sans-serif;
}

.pa-layout.pa-sidebar-collapsed {
  --pa-sidebar-width: 78px;
}

.pa-layout *,
.pa-layout *::before,
.pa-layout *::after {
  box-sizing: border-box;
}

.pa-content {
  display: flex;
  flex-direction: column;
  margin-left: var(--pa-sidebar-width);
  min-width: 0;
  min-height: 100vh;
  transition: margin-left .25s ease;
}

.pa-main-content {
  flex: 1;
  width: 100%;
  min-width: 0;
  padding: 30px;
}

.pa-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 19px 30px;
  background: #fff;
  border-top: 1px solid #e9edf0;
  color: #9ba4ad;
  font-size: 10px;
}

.pa-footer > span:last-child {
  display: flex;
  align-items: center;
  gap: 9px;
}

.pa-footer i {
  width: 4px;
  height: 4px;
  background: #ff7900;
  border-radius: 50%;
}

.pa-overlay {
  display: none;
}

@media (max-width: 1199px) {
  .pa-main-content {
    padding: 23px;
  }
}

@media (max-width: 991px) {
  .pa-content {
    margin-left: 0;
  }

  .pa-overlay {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1050;
    background: rgba(12, 17, 23, .55);
    backdrop-filter: blur(2px);
  }
}

@media (max-width: 575px) {
  .pa-main-content {
    padding: 16px;
  }

  .pa-footer {
    padding: 16px;
    justify-content: center;
    text-align: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pa-content {
    transition: none;
  }
}
</style>
